import type { FileRecord, PagedResponse, SingleResponse } from "@/types/domain";

type QueryValue = string | number | boolean | undefined | null;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Array<{ path: string; message: string }>;
  readonly traceId?: string;

  constructor(status: number, body: Record<string, unknown>) {
    const error = (body.error ?? {}) as Record<string, unknown>;
    super(String(error.message ?? `请求失败（${status}）`));
    this.name = "ApiError";
    this.status = status;
    this.code = String(error.code ?? "UNKNOWN_ERROR");
    this.fields = Array.isArray(error.fields)
      ? (error.fields as Array<{ path: string; message: string }>)
      : [];
    this.traceId = typeof body.traceId === "string" ? body.traceId : undefined;
  }
}

class RequestCoordinator {
  private controllers = new Set<AbortController>();

  track(): AbortController {
    const controller = new AbortController();
    this.controllers.add(controller);
    return controller;
  }

  release(controller: AbortController): void {
    this.controllers.delete(controller);
  }

  cancelAll(): void {
    this.controllers.forEach((controller) =>
      controller.abort(new DOMException("organization-changed", "AbortError")),
    );
    this.controllers.clear();
  }
}

export const requestCoordinator = new RequestCoordinator();

let csrfToken = "";
let organizationId = "";
let sessionExpiredHandler: (() => void) | undefined;

export function configureApi(context: {
  csrfToken?: string;
  organizationId?: string;
  onSessionExpired?: () => void;
}): void {
  if (context.csrfToken !== undefined) csrfToken = context.csrfToken;
  if (context.organizationId !== undefined)
    organizationId = context.organizationId;
  if (context.onSessionExpired !== undefined)
    sessionExpiredHandler = context.onSessionExpired;
}

function queryString(query?: Record<string, QueryValue>): string {
  if (!query) return "";
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      params.set(key, String(value));
  });
  const result = params.toString();
  return result ? `?${result}` : "";
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) return response.json();
  return response.text();
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit & {
    query?: Record<string, QueryValue>;
    idempotencyKey?: string;
  } = {},
): Promise<T> {
  const controller = requestCoordinator.track();
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (organizationId) headers.set("X-Org-Id", organizationId);
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken)
    headers.set("X-CSRF-Token", csrfToken);
  if (options.idempotencyKey)
    headers.set("Idempotency-Key", options.idempotencyKey);
  if (options.body && !(options.body instanceof FormData))
    headers.set("Content-Type", "application/json");

  try {
    const response = await fetch(
      `/api/v1${path}${queryString(options.query)}`,
      {
        ...options,
        headers,
        credentials: "include",
        cache: "no-store",
        signal: controller.signal,
      },
    );
    const body = await parseBody(response);
    if (!response.ok) {
      const record =
        typeof body === "object" && body !== null
          ? (body as Record<string, unknown>)
          : {};
      const error = new ApiError(response.status, record);
      if (response.status === 401 && error.code === "SESSION_EXPIRED")
        sessionExpiredHandler?.();
      throw error;
    }
    return body as T;
  } finally {
    requestCoordinator.release(controller);
  }
}

export const api = {
  get<T>(
    path: string,
    query?: Record<string, QueryValue>,
  ): Promise<SingleResponse<T>> {
    return apiRequest(path, { query });
  },
  list<T>(
    path: string,
    query?: Record<string, QueryValue>,
  ): Promise<PagedResponse<T>> {
    return apiRequest(path, { query });
  },
  post<T>(
    path: string,
    body?: unknown,
    idempotencyKey?: string,
  ): Promise<SingleResponse<T>> {
    return apiRequest(path, {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
      idempotencyKey,
    });
  },
  patch<T>(
    path: string,
    body: unknown,
    idempotencyKey?: string,
  ): Promise<SingleResponse<T>> {
    return apiRequest(path, {
      method: "PATCH",
      body: JSON.stringify(body),
      idempotencyKey,
    });
  },
  upload<T>(path: string, file: File): Promise<SingleResponse<T>> {
    const form = new FormData();
    form.append("file", file);
    return apiRequest(path, { method: "POST", body: form });
  },
};

export function humanizeError(error: unknown): string {
  if (error instanceof DOMException && error.name === "AbortError")
    return "请求已取消";
  if (error instanceof ApiError) {
    const fieldMessage = error.fields.map((field) => field.message).join("；");
    const trace = error.traceId ? `（追踪号：${error.traceId}）` : "";
    return `${fieldMessage || error.message}${trace}`;
  }
  return error instanceof Error ? error.message : "发生未知错误";
}

export async function fetchAuthenticatedBlob(
  path: string,
  options: { signal?: AbortSignal } = {},
): Promise<Blob> {
  const controller = requestCoordinator.track();
  const requestedOrganization = organizationId;
  const abort = () => controller.abort(options.signal?.reason);
  options.signal?.addEventListener("abort", abort, { once: true });
  if (options.signal?.aborted) abort();
  const assertActive = () => {
    if (controller.signal.aborted || organizationId !== requestedOrganization)
      throw new DOMException("文件请求已取消", "AbortError");
  };
  const headers = new Headers();
  if (requestedOrganization) headers.set("X-Org-Id", requestedOrganization);
  try {
    assertActive();
    const response = await fetch(
      `/api/v1${path}`,
      {
        credentials: "include",
        cache: "no-store",
        headers,
        signal: controller.signal,
      },
    );
    assertActive();
    if (!response.ok) {
      const body = await parseBody(response);
      assertActive();
      const error = new ApiError(response.status, typeof body === "object" && body !== null ? body as Record<string, unknown> : {});
      if (response.status === 401 && error.code === "SESSION_EXPIRED")
        sessionExpiredHandler?.();
      throw error;
    }
    const blob = await response.blob();
    assertActive();
    return blob;
  } finally {
    options.signal?.removeEventListener("abort", abort);
    requestCoordinator.release(controller);
  }
}

export function fetchFileBlob(fileId: string, options: { signal?: AbortSignal } = {}): Promise<Blob> {
  return fetchAuthenticatedBlob(`/files/${encodeURIComponent(fileId)}/content`, options);
}

export async function downloadFile(
  fileId: string,
  fileName: string,
): Promise<void> {
  const url = URL.createObjectURL(await fetchFileBlob(fileId));
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function waitForFileAvailable(
  file: FileRecord,
  attempts = 30,
): Promise<FileRecord> {
  let current = file;
  for (
    let attempt = 0;
    attempt < attempts && current.fileState !== "AVAILABLE";
    attempt += 1
  ) {
    if (current.fileState === "REJECTED") throw new Error("文件未通过安全检查");
    await new Promise((resolve) => setTimeout(resolve, 800));
    current = (await api.get<FileRecord>(`/files/${file.id}`)).data;
  }
  if (current.fileState !== "AVAILABLE")
    throw new Error("文件仍在检查中，请稍后从文件中心继续操作");
  return current;
}
